import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { ASSESSMENT_DIMENSIONS, SCORE_ANCHORS } from '../../../shared/dimensions';
import { ErrorNote, HeatCell, SectionTitle, Spinner } from '../../components/ui';

interface Row {
  dimension: string;
  score: number | null;
  evidence: string | null;
  notes: string | null;
}

export function AssessmentTab({ engagementId, reload }: { engagementId: string; reload: () => Promise<void> }) {
  const [rows, setRows] = useState<Map<string, Row> | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.get<{ rows: Row[] }>(`/api/engagements/${engagementId}/assessment`)
      .then((r) => setRows(new Map(r.rows.map((row) => [row.dimension, row]))))
      .catch(() => setError('Failed to load assessment.'));
  }, [engagementId]);

  const update = (dim: string, patch: Partial<Row>) => {
    setRows((m) => {
      if (!m) return m;
      const next = new Map(m);
      const cur = next.get(dim) ?? { dimension: dim, score: null, evidence: null, notes: null };
      next.set(dim, { ...cur, ...patch });
      return next;
    });
    setDirty(true);
  };

  async function save() {
    if (!rows) return;
    setSaving(true);
    setError('');
    try {
      await api.put(`/api/engagements/${engagementId}/assessment`, {
        rows: ASSESSMENT_DIMENSIONS.map((d) => {
          const r = rows.get(d.id);
          return { dimension: d.id, score: r?.score ?? null, evidence: r?.evidence ?? null, notes: r?.notes ?? null };
        }),
      });
      setDirty(false);
      setMessage('Assessment saved.');
      setTimeout(() => setMessage(''), 2500);
      await reload();
    } catch {
      setError('Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function seedNist() {
    setSeeding(true);
    setError('');
    try {
      const r = await api.post<{ added: number; weakDimensions: number }>(`/api/engagements/${engagementId}/nist/seed-from-assessment`);
      setMessage(`Seeded ${r.added} NIST mapping(s) from ${r.weakDimensions} weak dimension(s) — see the NIST tab.`);
      await reload();
    } catch {
      setError('Seeding failed.');
    } finally {
      setSeeding(false);
    }
  }

  const avg = useMemo(() => {
    if (!rows) return null;
    const scores = [...rows.values()].map((r) => r.score).filter((s): s is number => s != null);
    return scores.length > 0 ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10 : null;
  }, [rows]);

  if (error && !rows) return <ErrorNote message={error} />;
  if (!rows) return <Spinner />;

  return (
    <div>
      <div className="card mb-5">
        <SectionTitle
          right={
            <div className="flex items-center gap-2">
              {avg != null && <span className="text-sm text-slate-400">Average: <strong className="text-slate-100">{avg}/5</strong></span>}
              <button onClick={() => void seedNist()} disabled={seeding} className="btn-secondary !px-3 !py-1.5 text-xs">
                {seeding ? 'Seeding…' : '🛡️ Seed NIST gaps from weak scores'}
              </button>
              <button onClick={() => void save()} disabled={saving || !dirty} className="btn-primary !px-4 !py-1.5 text-xs">
                {saving ? 'Saving…' : dirty ? 'Save assessment' : 'Saved'}
              </button>
            </div>
          }
        >
          Maturity heatmap
        </SectionTitle>
        <div className="flex flex-wrap gap-3">
          {ASSESSMENT_DIMENSIONS.map((d) => (
            <div key={d.id} className="flex flex-col items-center gap-1">
              <HeatCell score={rows.get(d.id)?.score ?? null} />
              <span className="max-w-16 text-center text-[9px] leading-tight text-slate-500">{d.name.split(' & ')[0]}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-500">0 Absent · 1 Ad hoc · 2 Emerging · 3 Defined · 4 Managed · 5 Optimized</p>
        {message && <p className="mt-2 text-xs text-green-400">{message}</p>}
        {error && <ErrorNote message={error} />}
      </div>

      <div className="space-y-4">
        {ASSESSMENT_DIMENSIONS.map((d) => {
          const r = rows.get(d.id);
          const score = r?.score ?? null;
          return (
            <div key={d.id} className="card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="font-bold text-slate-100">{d.name}</h3>
                  <p className="mt-0.5 text-xs text-slate-400">{d.blurb}</p>
                </div>
                <select
                  className="input !w-64 !py-1.5 text-sm"
                  value={score == null ? '' : String(score)}
                  onChange={(e) => update(d.id, { score: e.target.value === '' ? null : Number(e.target.value) })}
                >
                  <option value="">Not assessed</option>
                  {[0, 1, 2, 3, 4, 5].map((s) => <option key={s} value={s}>{s} — {SCORE_ANCHORS[s]?.split(' — ')[0]}</option>)}
                </select>
              </div>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs font-semibold text-brand-400 hover:text-brand-300">
                  Guided questions & signals
                </summary>
                <div className="mt-2 grid gap-3 text-xs sm:grid-cols-2">
                  <div>
                    <p className="font-semibold text-slate-300">Ask:</p>
                    <ul className="mt-1 list-inside list-disc space-y-1 text-slate-400">
                      {d.questions.map((q) => <li key={q}>{q}</li>)}
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <p><span className="font-semibold text-red-400">Low (0–2):</span> <span className="text-slate-400">{d.lowSignals}</span></p>
                    <p><span className="font-semibold text-green-400">High (4–5):</span> <span className="text-slate-400">{d.highSignals}</span></p>
                    <p className="text-slate-500">NIST: {d.nistRefs.join(', ')}</p>
                  </div>
                </div>
              </details>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="label">Evidence</label>
                  <input className="input" value={r?.evidence ?? ''} placeholder="What did you observe / hear?"
                         onChange={(e) => update(d.id, { evidence: e.target.value })} />
                </div>
                <div>
                  <label className="label">Notes</label>
                  <input className="input" value={r?.notes ?? ''} placeholder="Context, caveats, follow-ups"
                         onChange={(e) => update(d.id, { notes: e.target.value })} />
                </div>
              </div>
              {score != null && score <= 2 && (
                <div className="mt-3 rounded-lg border border-amber-900/60 bg-amber-950/20 p-3 text-xs">
                  <p className="font-semibold text-amber-300">Recommended actions (scored {score}/5):</p>
                  <ul className="mt-1 list-inside list-disc space-y-0.5 text-slate-300">
                    {d.actions.map((a) => <li key={a}>{a}</li>)}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
